import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const Card = ({ icon, title, children, testid }) => (
  <section className="max-w-2xl mx-auto px-6 py-16 sm:py-24">
    <div className="bg-white rounded-2xl border border-[#183C35]/10 p-8 sm:p-12 text-center fade-up" data-testid={testid}>
      {icon}
      <h1 className="font-serif-d text-[30px] sm:text-[38px] font-semibold text-[#183C35] mt-4">{title}</h1>
      {children}
    </div>
  </section>
);

const btn = "rounded-full bg-[#183C35] text-[#F7F3EA] px-6 py-3 font-medium hover:bg-[#21524A] transition-colors disabled:opacity-60";

const Success = () => {
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const [st, setSt] = useState({ state: "checking" });
  useEffect(() => {
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (stop) return;
        if (data.payment_status === "paid") return setSt({ state: "paid", ref: data.ref });
        if (["expired", "failed"].includes(data.payment_status)) return setSt({ state: "failed", ref: data.ref });
      } catch { if (stop) return; }
      if (++tries < 15) setTimeout(poll, 2000); else setSt({ state: "timeout" });
    };
    if (sid) poll(); else setSt({ state: "failed" });
    return () => { stop = true; };
  }, [sid]);
  if (st.state === "checking") return <Card testid="payment-checking" title="Confirming your payment..." icon={<Loader2 className="w-12 h-12 mx-auto text-[#3E7F8E] animate-spin" />}><p className="mt-3 text-[#202522]/65">Please don't close this page.</p></Card>;
  if (st.state === "paid") return (
    <Card testid="payment-success" title="Payment received, booking confirmed!" icon={<CheckCircle2 className="w-14 h-14 mx-auto text-[#3E7F8E]" />}>
      <p className="mt-3 text-[16px] text-[#202522]/70">Your confirmation and receipt are on their way by email. Please check your spam folder if you don't see them in a few minutes.</p>
      <p className="mt-6 inline-block rounded-full bg-[#F7F3EA] px-5 py-2 text-[15px] text-[#183C35]">Reference: <strong data-testid="payment-booking-ref">{st.ref}</strong></p>
      <div className="mt-8"><Link to="/" className={btn} data-testid="payment-home-btn">Back to home</Link></div>
    </Card>
  );
  return (
    <Card testid="payment-not-confirmed" title={st.state === "timeout" ? "Still waiting for your payment" : "Payment not completed"} icon={<XCircle className="w-14 h-14 mx-auto text-[#B42318]" />}>
      <p className="mt-3 text-[16px] text-[#202522]/70">{st.state === "timeout" ? "We haven't received confirmation yet. If you were charged, please contact us on WhatsApp." : "Your booking is not confirmed because the payment did not go through."}</p>
      <div className="mt-8"><Link to="/book" className={btn}>Book again</Link></div>
    </Card>
  );
};

const Cancel = () => {
  const [sp] = useSearchParams();
  const ref = sp.get("ref");
  const [busy, setBusy] = useState(false);
  const retry = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { ref, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { setBusy(false); }
  };
  return (
    <Card testid="payment-cancelled" title="Payment cancelled" icon={<XCircle className="w-14 h-14 mx-auto text-[#B42318]" />}>
      <p className="mt-3 text-[16px] text-[#202522]/70">No payment was taken, so your booking is <strong>not confirmed</strong>. You can try the payment again or start a new booking.</p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {ref && <button onClick={retry} disabled={busy} className={btn} data-testid="payment-retry-btn">{busy ? "Opening payment..." : "Try payment again"}</button>}
        <Link to="/book" className="rounded-full border border-[#183C35]/20 text-[#183C35] px-6 py-3 font-medium hover:bg-[#F7F3EA] transition-colors" data-testid="payment-new-booking-btn">New booking</Link>
      </div>
    </Card>
  );
};

export default function PaymentResult({ mode }) {
  return mode === "success" ? <Success /> : <Cancel />;
}
